import { CancelablePromise, OpenAPI, request as __request } from '/src/api';
import { Transaction } from '/src/lib/models';
import type { TListTransactionsRequest, TListTransactionsResponse } from '/src/lib/types';
import { isNumber } from '/src/utils';

export class TransactionService {
  private static _interceptor = (transactions: Transaction[]) => {
    // Prepare
    const totals: Record<string, any> = {
      display: {},
      feesCharged: 0,
      feeTotal: 0,
      feesPaid: 0,
      interestCharged: 0,
      interestPaid: 0,
      principalPaid: 0,
      currentPrincipal: 0,
      advanceAmount: 0,
      adjustmentAmount: 0,
      paymentAmount: 0,
    };
    let transaction;

    // Sort the history by date (newest at index 0)
    transactions.sort((a, b) => new Date(b.sequenceNumber).getTime() - new Date(a.sequenceNumber).getTime());

    // Prepare finished transactions array
    const formattedTransactions = [];

    // Loop through the transactions and perform calculations
    for (let i = 0; i < transactions.length; i++) {
      transaction = transactions[i];

      if (transaction.__t !== 'adjustments') {
        // Calculate
        transaction.feeTotal =
          (transaction.originationFee ?? 0) +
          (transaction.diligenceFee ?? 0) +
          (transaction.lateFee ?? 0) +
          (transaction.facilityFee ?? 0) +
          (transaction.adminFee ?? 0);
        transaction.feesDue = transaction.feeBalance + transaction.feeTotal;
        transaction.feesDue = transaction.appliedToFees
          ? transaction.feesDue - transaction.appliedToFees
          : transaction.feesDue;
        transaction.interestDue = transaction.interestBalance + transaction.interestCharge;
        transaction.interestDue = transaction.appliedToInterest
          ? transaction.interestDue - transaction.appliedToInterest
          : transaction.interestDue;

        // Tally
        totals.feeTotal += transaction.feeTotal;
        totals.feesCharged += transaction.feeTotal;
        totals.feesPaid = isNumber(transaction.appliedToFees)
          ? totals.feesPaid + transaction.appliedToFees
          : totals.feesPaid;
        totals.interestCharged += transaction.interestCharge;
        totals.interestPaid = transaction.appliedToInterest
          ? totals.interestPaid + transaction.appliedToInterest
          : totals.interestPaid;

        // Parse
        transaction.effectiveDate = new Date(transaction.effectiveDate);

        // Filter
        // transaction.display = {};

        switch (transaction.__t) {
          case 'advances':
            // Calculate
            transaction.advanceAmount = transaction.amount;
            transaction.newPrincipalBalance = transaction.principalBalance + transaction.amount;

            // Tally
            totals.advanceAmount += transaction.amount;
            totals.currentPrincipal = transaction.newPrincipalBalance;

            break;

          case 'payments':
            // Calculate
            transaction.paymentAmount = transaction.amount;
            transaction.newPrincipalBalance = transaction.principalBalance - transaction.appliedToPrincipal;

            // Tally
            totals.paymentAmount += transaction.amount;
            totals.currentPrincipal = transaction.newPrincipalBalance;
            totals.principalPaid += transaction.appliedToPrincipal;

            break;

          case 'statements':
            // Calculate
            transaction.newPrincipalBalance = transaction.principalBalance;

            break;
        }

        // Store
        formattedTransactions.push(transaction);
      }
    }

    // Store updated transactions
    transactions = formattedTransactions;

    if (transactions && transactions[0]) {
      if (transactions[0].newPrincipalBalance) totals.currentPrincipal = transactions[0].newPrincipalBalance;
      if (transactions[0].assetValue) totals.currentAssets = transactions[0].assetValue;
      if (transactions[0].currentAvailableCredit)
        totals.currentAvailableCredit = transactions[0].currentAvailableCredit;
    }

    // Return
    return { transactions, totals };
  };

  /**
   * Get Loan Transactions
   */
  public static async query(req: TListTransactionsRequest): CancelablePromise<TListTransactionsResponse> {
    return this._interceptor(
      await __request(OpenAPI, {
        method: 'GET',
        url: '/transactions',
        query: req,
      })
    );
  }
}
